/**
 * InfinitSales - WhatsApp Business Templates Page
 * Manage Meta message templates (create, list, delete, send, view history)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ScrollText, Plus, Trash2, X, Loader2, Check, AlertCircle,
  PanelLeftOpen, PanelLeftClose, Search, RefreshCw,
  Send, History, Phone, ChevronLeft, ChevronRight,
  CheckCheck, Clock, XCircle, ArrowUpRight,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import templateService from '../../services/global/templateService';
import TemplateFillPanel from '../../components/messages/TemplateFillPanel';
import {
  templateRequiresParams, positionalParamsInText, fillBodyText,
} from '../../utils/templateVariables';
import { analyzeTemplate } from '../../utils/templateParams';
import useFeatureFlags from '../../hooks/useFeatureFlags';


const TemplatesPage = ({ onToggleNav, isNavOpen }) => {
  const { colors } = useTheme();
  const { flags } = useFeatureFlags();
  const templateUi = Boolean(flags.template_params_ui);

  // State
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Create form
  const [formName, setFormName] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formCategory, setFormCategory] = useState('UTILITY');
  const [formLanguage, setFormLanguage] = useState('pt_BR');
  // Estrutura completa: cabeçalho de texto, rodapé, botões e exemplos das
  // variáveis (a Meta rejeita {{n}} sem exemplo).
  const [formHeader, setFormHeader] = useState('');
  const [formHeaderExample, setFormHeaderExample] = useState('');
  const [formFooter, setFormFooter] = useState('');
  const [formButtons, setFormButtons] = useState([]);
  const [formBodyExamples, setFormBodyExamples] = useState([]);

  // Send modal
  const [sendModal, setSendModal] = useState(null); // template object or null
  const [sendPhone, setSendPhone] = useState('');
  const [sending, setSending] = useState(false);

  // History modal
  const [historyModal, setHistoryModal] = useState(null); // template name or null
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPageSize] = useState(20);
  const [historyPhoneFilter, setHistoryPhoneFilter] = useState('');
  const debounceRef = useRef(null);

  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await templateService.list();
      setTemplates(data.templates || []);
    } catch (err) {
      setError('Erro ao carregar templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Variáveis {{n}} presentes no corpo do formulário (dirige os inputs de
  // exemplo e a prévia).
  const formBodyParams = positionalParamsInText(formBody);

  const resetCreateForm = () => {
    setFormName('');
    setFormBody('');
    setFormCategory('UTILITY');
    setFormHeader('');
    setFormHeaderExample('');
    setFormFooter('');
    setFormButtons([]);
    setFormBodyExamples([]);
  };

  // Create template
  const handleCreate = async () => {
    if (!formName.trim() || !formBody.trim()) return;

    try {
      setCreating(true);
      setError(null);
      const payload = {
        name: formName.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
        body_text: formBody.trim(),
        category: formCategory,
        language: formLanguage,
      };
      if (formHeader.trim()) payload.header_text = formHeader.trim();
      if (formHeaderExample.trim()) payload.header_example = formHeaderExample.trim();
      if (formFooter.trim()) payload.footer_text = formFooter.trim();
      if (formButtons.length) {
        payload.buttons = formButtons.map((b) => ({
          type: b.type,
          text: (b.text || '').trim(),
          ...(b.type === 'URL' ? { url: (b.url || '').trim(), example: (b.example || '').trim() || undefined } : {}),
          ...(b.type === 'PHONE_NUMBER' ? { phone_number: (b.phone_number || '').trim() } : {}),
        }));
      }
      if (formBodyParams.length) {
        payload.body_examples = formBodyParams.map((n, i) => (formBodyExamples[i] || '').trim());
      }
      await templateService.create(payload);
      setShowCreateModal(false);
      resetCreateForm();
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao criar template');
    } finally {
      setCreating(false);
    }
  };

  // Delete template
  const handleDelete = async (name) => {
    if (!window.confirm(`Deletar template "${name}"?`)) return;

    try {
      setDeleting(name);
      await templateService.delete(name);
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao deletar template');
    } finally {
      setDeleting(null);
    }
  };

  // Send template (extra = preenchimento de variáveis, quando o template tem)
  const handleSend = async (extra = null) => {
    if (!sendPhone.trim() || !sendModal) return;

    try {
      setSending(true);
      setError(null);
      await templateService.send(sendModal.name, sendPhone.trim(), extra || {});
      setSuccessMsg(`Template enviado para ${sendPhone}`);
      setSendModal(null);
      setSendPhone('');
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao enviar template');
    } finally {
      setSending(false);
    }
  };

  // Load history logs
  const loadHistoryLogs = useCallback(async (templateName, page = 1, phone = '') => {
    try {
      setHistoryLoading(true);
      const data = await templateService.getLogs(templateName, {
        page,
        pageSize: historyPageSize,
        phone,
      });
      setHistoryLogs(data.logs || []);
      setHistoryTotal(data.total || 0);
      setHistoryPage(data.page || 1);
    } catch (err) {
      console.error('Error loading logs:', err);
      setHistoryLogs([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPageSize]);

  // Open history modal
  const openHistory = (templateName) => {
    setHistoryModal(templateName);
    setHistoryPhoneFilter('');
    setHistoryPage(1);
    loadHistoryLogs(templateName, 1, '');
  };

  // History phone filter with debounce
  const handleHistoryPhoneChange = (value) => {
    setHistoryPhoneFilter(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setHistoryPage(1);
      loadHistoryLogs(historyModal, 1, value);
    }, 500);
  };

  // History pagination
  const handleHistoryPageChange = (newPage) => {
    setHistoryPage(newPage);
    loadHistoryLogs(historyModal, newPage, historyPhoneFilter);
  };

  const totalPages = Math.ceil(historyTotal / historyPageSize);

  // Filter templates
  const filtered = templates.filter(t =>
    t.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Status badge
  const StatusBadge = ({ status }) => {
    const map = {
      APPROVED: { bg: '#10b98120', color: '#10b981', label: 'Aprovado' },
      PENDING: { bg: '#f59e0b20', color: '#f59e0b', label: 'Pendente' },
      REJECTED: { bg: '#ef444420', color: '#ef4444', label: 'Rejeitado' },
    };
    const s = map[status] || { bg: '#6b728020', color: '#6b7280', label: status || 'Desconhecido' };
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
        fontWeight: 600, backgroundColor: s.bg, color: s.color,
      }}>
        {s.label}
      </span>
    );
  };

  // Quality tag
  const QualityTag = ({ score }) => {
    const scoreStr = typeof score === 'object' ? score?.score : score;
    const map = {
      HIGH: { bg: '#10b98118', color: '#10b981', label: 'Alta' },
      MEDIUM: { bg: '#f59e0b18', color: '#f59e0b', label: 'Media' },
      LOW: { bg: '#ef444418', color: '#ef4444', label: 'Baixa' },
    };
    const key = typeof scoreStr === 'string' ? scoreStr.toUpperCase() : null;
    const q = map[key] || { bg: `${colors.textTertiary}15`, color: colors.textTertiary, label: scoreStr || 'N/A' };
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        padding: '2px 8px', borderRadius: '6px', fontSize: '11px',
        fontWeight: 500, backgroundColor: q.bg, color: q.color,
      }}>
        {q.label}
      </span>
    );
  };

  // Log status badge
  const LogStatusBadge = ({ status }) => {
    const map = {
      sent: { bg: '#3b82f618', color: '#3b82f6', label: 'Enviado', icon: <ArrowUpRight size={12} /> },
      delivered: { bg: '#10b98118', color: '#10b981', label: 'Entregue', icon: <Check size={12} /> },
      read: { bg: '#6366f118', color: '#6366f1', label: 'Lido', icon: <CheckCheck size={12} /> },
      failed: { bg: '#ef444418', color: '#ef4444', label: 'Falhou', icon: <XCircle size={12} /> },
    };
    const s = map[status] || { bg: '#6b728018', color: '#6b7280', label: status || '?', icon: <Clock size={12} /> };
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
        fontWeight: 600, backgroundColor: s.bg, color: s.color,
      }}>
        {s.icon} {s.label}
      </span>
    );
  };

  // Get body text from components
  const getBodyText = (template) => {
    const body = template.components?.find(c => c.type === 'BODY');
    return body?.text || '';
  };

  // Format timestamp
  const formatDate = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      return d.toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  const styles = {
    container: {
      flex: 1, display: 'flex', flexDirection: 'column',
      backgroundColor: colors.bgSecondary, height: '100vh', overflow: 'hidden',
    },
    header: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 24px', backgroundColor: colors.bgPrimary,
      boxShadow: colors.shadowSm, minHeight: '60px', zIndex: 1,
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
    headerIcon: {
      width: '40px', height: '40px', borderRadius: '10px',
      backgroundColor: colors.accentPrimary, display: 'flex',
      alignItems: 'center', justifyContent: 'center', color: 'white',
    },
    content: { flex: 1, overflow: 'auto', padding: '24px' },
    searchBar: {
      display: 'flex', alignItems: 'center', gap: 6,
      backgroundColor: colors.bgTertiary, borderRadius: 20,
      padding: '0 12px', width: 300, height: 38,
    },
    searchInput: {
      flex: 1, padding: '8px 4px', border: 'none', background: 'transparent',
      color: colors.textPrimary, fontSize: 14, outline: 'none',
    },
    btn: {
      display: 'flex', alignItems: 'center', gap: '6px',
      padding: '8px 16px', borderRadius: '8px', border: 'none',
      cursor: 'pointer', fontSize: '13px', fontWeight: 500,
    },
    btnPrimary: {
      backgroundColor: colors.accentPrimary, color: '#fff',
    },
    btnGhost: {
      backgroundColor: 'transparent', color: colors.textSecondary,
      border: `1px solid ${colors.borderColor}`,
    },
    // Grid layout
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '16px',
    },
    card: {
      backgroundColor: colors.bgPrimary, borderRadius: '12px',
      border: `1px solid ${colors.borderColor}`, padding: '16px',
      display: 'flex', flexDirection: 'column',
      transition: 'border-color 0.2s',
    },
    cardHeader: {
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      marginBottom: '10px', gap: '8px',
    },
    cardName: {
      fontSize: '14px', fontWeight: 600, color: colors.textPrimary,
      fontFamily: 'monospace', wordBreak: 'break-all',
    },
    cardBody: {
      fontSize: '13px', color: colors.textSecondary, lineHeight: '1.5',
      padding: '10px 12px', borderRadius: '8px',
      backgroundColor: colors.bgSecondary, whiteSpace: 'pre-wrap',
      maxHeight: '72px', overflow: 'hidden', flex: 1,
    },
    cardTags: {
      display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
      marginTop: '10px',
    },
    tag: {
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: '6px', fontSize: '11px',
      fontWeight: 500, backgroundColor: `${colors.textTertiary}12`,
      color: colors.textTertiary,
    },
    cardActions: {
      display: 'flex', alignItems: 'center', gap: '6px',
      marginTop: '12px', paddingTop: '12px',
      borderTop: `1px solid ${colors.borderColor}`,
    },
    actionBtn: {
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
      padding: '6px 12px', borderRadius: '8px', border: 'none',
      cursor: 'pointer', fontSize: '12px', fontWeight: 500,
      backgroundColor: 'transparent', color: colors.textSecondary,
      transition: 'background-color 0.15s',
    },
    // Modal
    overlay: {
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    },
    modal: {
      backgroundColor: colors.bgPrimary, borderRadius: '16px',
      width: '500px', maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto',
      border: `1px solid ${colors.borderColor}`,
    },
    modalLarge: {
      backgroundColor: colors.bgPrimary, borderRadius: '16px',
      width: '640px', maxWidth: '90vw', maxHeight: '85vh',
      border: `1px solid ${colors.borderColor}`,
      display: 'flex', flexDirection: 'column',
    },
    modalHeader: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
    },
    modalBody: { padding: '20px' },
    modalFooter: {
      display: 'flex', justifyContent: 'flex-end', gap: '8px',
      padding: '16px 20px', borderTop: `1px solid ${colors.borderColor}`,
    },
    formGroup: { marginBottom: '16px' },
    label: {
      display: 'block', fontSize: '13px', fontWeight: 500,
      color: colors.textPrimary, marginBottom: '6px',
    },
    input: {
      width: '100%', padding: '8px 12px', borderRadius: '8px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
      color: colors.textPrimary, fontSize: '14px', outline: 'none',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%', padding: '8px 12px', borderRadius: '8px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
      color: colors.textPrimary, fontSize: '14px', outline: 'none',
      minHeight: '120px', resize: 'vertical', fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    select: {
      width: '100%', padding: '8px 12px', borderRadius: '8px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
      color: colors.textPrimary, fontSize: '14px', outline: 'none',
      boxSizing: 'border-box',
    },
    navToggleBtn: {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '40px', height: '40px', border: 'none', background: 'transparent',
      color: colors.textSecondary, borderRadius: '8px', cursor: 'pointer',
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          {onToggleNav && (
            <button onClick={onToggleNav} style={styles.navToggleBtn}>
              {isNavOpen ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
            </button>
          )}
          <div style={styles.headerIcon}><ScrollText size={20} /></div>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
              Templates
            </h2>
            <p style={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}>
              {templates.length} template{templates.length !== 1 ? 's' : ''} da Meta
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={styles.searchBar}>
            <Search size={16} color={colors.textTertiary} />
            <input
              placeholder="Buscar templates..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{ ...styles.btn, ...styles.btnPrimary }}
          >
            <Plus size={16} /> Novo Template
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Success message */}
        {successMsg && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
            backgroundColor: '#10b98120', color: '#10b981', fontSize: '13px',
          }}>
            <Check size={16} /> {successMsg}
            <button
              onClick={() => setSuccessMsg(null)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#10b981', cursor: 'pointer' }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
            backgroundColor: '#ef444420', color: '#ef4444', fontSize: '13px',
          }}>
            <AlertCircle size={16} /> {error}
            <button
              onClick={() => setError(null)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
            <Loader2 size={24} color={colors.accentPrimary} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '60px', gap: '16px',
          }}>
            <div style={{
              width: '80px', height: '80px', borderRadius: '20px',
              backgroundColor: `${colors.accentPrimary}15`, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <ScrollText size={36} color={colors.accentPrimary} />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
              {searchTerm ? 'Nenhum template encontrado' : 'Nenhum template criado ainda'}
            </h3>
            <p style={{ fontSize: '14px', color: colors.textSecondary, textAlign: 'center', maxWidth: '360px', margin: 0 }}>
              {searchTerm ? 'Tente buscar com outro termo.' : 'Crie seu primeiro template para enviar mensagens padronizadas.'}
            </p>
          </div>
        ) : (
          /* Grid of cards */
          <div style={styles.grid}>
            {filtered.map(template => {
              const unsupported = templateUi ? analyzeTemplate(template).unsupported : null;
              return (
              <div
                key={`${template.name}-${template.language}`}
                style={styles.card}
                onMouseEnter={e => e.currentTarget.style.borderColor = colors.accentPrimary + '60'}
                onMouseLeave={e => e.currentTarget.style.borderColor = colors.borderColor}
              >
                <div style={styles.cardHeader}>
                  <span style={styles.cardName}>{template.name}</span>
                  <StatusBadge status={template.status} />
                </div>

                <div style={styles.cardBody}>{getBodyText(template)}</div>

                {/* Motivo da rejeição, quando a Meta devolve */}
                {template.status === 'REJECTED' && template.rejected_reason && (
                  <div style={{
                    fontSize: '11px', color: '#ef4444', padding: '6px 8px',
                    backgroundColor: '#ef444412', borderRadius: '6px', marginBottom: '8px',
                  }}>
                    Motivo: {template.rejected_reason}
                  </div>
                )}

                {/* Tags row */}
                <div style={styles.cardTags}>
                  <span style={styles.tag}>{template.language}</span>
                  <span style={styles.tag}>{template.category === 'UTILITY' ? 'Utilidade' : template.category === 'MARKETING' ? 'Marketing' : template.category}</span>
                  {template.quality_score && (
                    <QualityTag score={template.quality_score} />
                  )}
                </div>

                {/* Actions */}
                <div style={styles.cardActions}>
                  <button
                    onClick={() => { setSendModal(template); setSendPhone(''); }}
                    style={{ ...styles.actionBtn, color: colors.accentPrimary }}
                    disabled={template.status !== 'APPROVED' || Boolean(unsupported)}
                    title={template.status !== 'APPROVED'
                      ? 'Apenas templates aprovados podem ser enviados'
                      : (unsupported || 'Enviar template')}
                  >
                    <Send size={14} /> Enviar
                  </button>
                  <button
                    onClick={() => openHistory(template.name)}
                    style={styles.actionBtn}
                    title="Ver histórico de envios"
                  >
                    <History size={14} /> Histórico
                  </button>
                  <button
                    onClick={() => handleDelete(template.name)}
                    disabled={deleting === template.name}
                    style={{ ...styles.actionBtn, marginLeft: 'auto', color: '#ef4444' }}
                    title="Deletar template"
                  >
                    {deleting === template.name
                      ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      : <Trash2 size={14} />
                    }
                  </button>
                </div>
                {unsupported && (
                  <div style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '8px', lineHeight: 1.4 }}>
                    {unsupported}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ======================== */}
      {/* Create Modal */}
      {/* ======================== */}
      {showCreateModal && (
        <div style={styles.overlay} onClick={() => !creating && setShowCreateModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
                Novo Template
              </h3>
              <button
                onClick={() => !creating && setShowCreateModal(false)}
                style={{ background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Nome do Template</label>
                <input
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="ex: boas_vindas_desafio"
                  style={styles.input}
                />
                <p style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '4px' }}>
                  Apenas letras minusculas, numeros e underscores
                </p>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Cabeçalho (opcional)</label>
                <input
                  value={formHeader}
                  onChange={e => setFormHeader(e.target.value)}
                  placeholder="ex: Pedido confirmado (aceita uma variável {{1}})"
                  style={styles.input}
                  maxLength={60}
                />
                {positionalParamsInText(formHeader).length > 0 && (
                  <input
                    value={formHeaderExample}
                    onChange={e => setFormHeaderExample(e.target.value)}
                    placeholder="Exemplo do {{1}} do cabeçalho (obrigatório para a Meta)"
                    style={{ ...styles.input, marginTop: '6px' }}
                  />
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Texto do Template</label>
                <textarea
                  value={formBody}
                  onChange={e => setFormBody(e.target.value)}
                  placeholder={'Digite o texto... Use {{1}}, {{2}} para variáveis preenchidas no envio.'}
                  style={styles.textarea}
                  maxLength={1024}
                />
                <p style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '4px' }}>
                  {formBody.length}/1024 caracteres
                  {formBodyParams.length > 0 && ` · ${formBodyParams.length} variável(is)`}
                </p>
              </div>
              {formBodyParams.length > 0 && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Exemplos das variáveis (a Meta exige)</label>
                  {formBodyParams.map((n, i) => (
                    <input
                      key={n}
                      value={formBodyExamples[i] || ''}
                      onChange={e => setFormBodyExamples(prev => {
                        const next = [...prev];
                        next[i] = e.target.value;
                        return next;
                      })}
                      placeholder={`Exemplo do {{${n}}} — ex.: ${n === 1 ? 'Ana' : 'valor real'}`}
                      style={{ ...styles.input, marginBottom: '6px' }}
                    />
                  ))}
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.label}>Rodapé (opcional)</label>
                <input
                  value={formFooter}
                  onChange={e => setFormFooter(e.target.value)}
                  placeholder="ex: Equipe InfinitSales"
                  style={styles.input}
                  maxLength={60}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Botões (opcional)</label>
                {formButtons.map((b, i) => (
                  <div key={i} style={{
                    display: 'flex', flexDirection: 'column', gap: '6px',
                    padding: '8px', borderRadius: '8px', marginBottom: '6px',
                    border: `1px solid ${colors.borderColor}`,
                  }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <select
                        value={b.type}
                        onChange={e => setFormButtons(prev => prev.map((x, j) => j === i ? { ...x, type: e.target.value } : x))}
                        style={{ ...styles.select, flex: 1 }}
                      >
                        <option value="QUICK_REPLY">Resposta rápida</option>
                        <option value="URL">Link</option>
                        <option value="PHONE_NUMBER">Telefone</option>
                      </select>
                      <button
                        onClick={() => setFormButtons(prev => prev.filter((_, j) => j !== i))}
                        style={{
                          background: 'none', border: `1px solid ${colors.borderColor}`,
                          borderRadius: '8px', color: '#ef4444', cursor: 'pointer', padding: '0 10px',
                        }}
                        title="Remover botão"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <input
                      value={b.text || ''}
                      onChange={e => setFormButtons(prev => prev.map((x, j) => j === i ? { ...x, text: e.target.value } : x))}
                      placeholder="Texto do botão (até 25 caracteres)"
                      style={styles.input}
                      maxLength={25}
                    />
                    {b.type === 'URL' && (
                      <>
                        <input
                          value={b.url || ''}
                          onChange={e => setFormButtons(prev => prev.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                          placeholder={'https://seusite.com/pedido/{{1}} (variável opcional, no final)'}
                          style={styles.input}
                        />
                        {positionalParamsInText(b.url).length > 0 && (
                          <input
                            value={b.example || ''}
                            onChange={e => setFormButtons(prev => prev.map((x, j) => j === i ? { ...x, example: e.target.value } : x))}
                            placeholder="Exemplo de URL completa (obrigatório para a Meta)"
                            style={styles.input}
                          />
                        )}
                      </>
                    )}
                    {b.type === 'PHONE_NUMBER' && (
                      <input
                        value={b.phone_number || ''}
                        onChange={e => setFormButtons(prev => prev.map((x, j) => j === i ? { ...x, phone_number: e.target.value } : x))}
                        placeholder="+5551999999999"
                        style={styles.input}
                      />
                    )}
                  </div>
                ))}
                {formButtons.length < 10 && (
                  <button
                    onClick={() => setFormButtons(prev => [...prev, { type: 'QUICK_REPLY', text: '' }])}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      background: 'none', border: `1px dashed ${colors.borderColor}`,
                      borderRadius: '8px', color: colors.textSecondary, cursor: 'pointer',
                      padding: '6px 12px', fontSize: '12px',
                    }}
                  >
                    <Plus size={13} /> Adicionar botão
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>Categoria</label>
                  <select
                    value={formCategory}
                    onChange={e => setFormCategory(e.target.value)}
                    style={styles.select}
                  >
                    <option value="UTILITY">Utilidade</option>
                    <option value="MARKETING">Marketing</option>
                  </select>
                </div>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>Idioma</label>
                  <select
                    value={formLanguage}
                    onChange={e => setFormLanguage(e.target.value)}
                    style={styles.select}
                  >
                    <option value="pt_BR">Portugues (BR)</option>
                    <option value="en_US">English (US)</option>
                    <option value="es">Espanol</option>
                  </select>
                </div>
              </div>
              {/* Prévia no formato do WhatsApp, com os exemplos aplicados */}
              {formBody.trim() && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Prévia</label>
                  <div style={{
                    borderRadius: '12px', padding: '10px 12px',
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.borderColor}`,
                    maxWidth: '340px',
                  }}>
                    {formHeader.trim() && (
                      <div style={{ fontSize: '13px', fontWeight: 700, color: colors.textPrimary, marginBottom: '4px' }}>
                        {fillBodyText(formHeader, [formHeaderExample])}
                      </div>
                    )}
                    <div style={{ fontSize: '13px', color: colors.textPrimary, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                      {fillBodyText(formBody, formBodyParams.map((n, i) => formBodyExamples[i] || ''))}
                    </div>
                    {formFooter.trim() && (
                      <div style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '6px' }}>
                        {formFooter}
                      </div>
                    )}
                    {formButtons.filter(b => (b.text || '').trim()).length > 0 && (
                      <div style={{ marginTop: '8px', borderTop: `1px solid ${colors.borderColor}` }}>
                        {formButtons.filter(b => (b.text || '').trim()).map((b, i) => (
                          <div key={i} style={{
                            textAlign: 'center', padding: '7px 0', fontSize: '13px',
                            color: '#3b82f6', fontWeight: 600,
                            borderBottom: i < formButtons.length - 1 ? `1px solid ${colors.borderColor}` : 'none',
                          }}>
                            {b.text}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
                style={{ ...styles.btn, ...styles.btnGhost }}
              >
                Cancelar
              </button>
              {(() => {
                const missingExamples =
                  formBodyParams.some((n, i) => !(formBodyExamples[i] || '').trim())
                  || (positionalParamsInText(formHeader).length > 0 && !formHeaderExample.trim());
                const disabled = creating || !formName.trim() || !formBody.trim() || missingExamples;
                return (
                  <button
                    onClick={handleCreate}
                    disabled={disabled}
                    title={missingExamples ? 'Preencha um exemplo para cada variável: a Meta rejeita sem exemplos' : undefined}
                    style={{
                      ...styles.btn, ...styles.btnPrimary,
                      opacity: disabled ? 0.6 : 1,
                    }}
                  >
                    {creating ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={14} />}
                    {creating ? 'Criando...' : 'Criar Template'}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ======================== */}
      {/* Send Modal */}
      {/* ======================== */}
      {sendModal && (
        <div style={styles.overlay} onClick={() => !sending && setSendModal(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
                  Enviar Template
                </h3>
                <p style={{ fontSize: '12px', color: colors.textTertiary, margin: '2px 0 0' }}>
                  {sendModal.name}
                </p>
              </div>
              <button
                onClick={() => !sending && setSendModal(null)}
                style={{ background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={styles.modalBody}>
              {/* Preview */}
              <div style={{
                padding: '12px 16px', borderRadius: '10px',
                backgroundColor: colors.bgSecondary, marginBottom: '16px',
                fontSize: '13px', color: colors.textSecondary, lineHeight: '1.5',
                whiteSpace: 'pre-wrap', maxHeight: '120px', overflow: 'auto',
                border: `1px solid ${colors.borderColor}`,
              }}>
                {getBodyText(sendModal)}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <Phone size={14} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '4px' }} />
                  Telefone do destinatário
                </label>
                <input
                  value={sendPhone}
                  onChange={e => setSendPhone(e.target.value)}
                  placeholder="5551999999999"
                  style={styles.input}
                  autoFocus
                />
                <p style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '4px' }}>
                  Código do país + DDD + número (sem espaços ou traços)
                </p>
              </div>

              {/* Template com variáveis: preenchimento obrigatório antes do envio */}
              {templateRequiresParams(sendModal) && (
                <TemplateFillPanel
                  template={sendModal}
                  colors={colors}
                  contactName={null}
                  contactPhone={sendPhone.trim().length >= 10 ? sendPhone.trim() : ''}
                  sending={sending}
                  onSend={(t, extra) => {
                    if (!sendPhone.trim()) {
                      setError('Informe o telefone do destinatário.');
                      return;
                    }
                    handleSend(extra);
                  }}
                  onCancel={() => setSendModal(null)}
                />
              )}
            </div>
            {/* Com variáveis, o envio acontece pelo painel de preenchimento */}
            {!templateRequiresParams(sendModal) && (
              <div style={styles.modalFooter}>
                <button
                  onClick={() => setSendModal(null)}
                  disabled={sending}
                  style={{ ...styles.btn, ...styles.btnGhost }}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleSend()}
                  disabled={sending || !sendPhone.trim()}
                  style={{
                    ...styles.btn, ...styles.btnPrimary,
                    opacity: sending || !sendPhone.trim() ? 0.6 : 1,
                  }}
                >
                  {sending ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />}
                  {sending ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ======================== */}
      {/* History Modal */}
      {/* ======================== */}
      {historyModal && (
        <div style={styles.overlay} onClick={() => setHistoryModal(null)}>
          <div style={styles.modalLarge} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={styles.modalHeader}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
                  Histórico de Envios
                </h3>
                <p style={{ fontSize: '12px', color: colors.textTertiary, margin: '2px 0 0', fontFamily: 'monospace' }}>
                  {historyModal}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => loadHistoryLogs(historyModal, historyPage, historyPhoneFilter)}
                  style={{ ...styles.actionBtn, padding: '6px' }}
                  title="Atualizar"
                >
                  <RefreshCw size={14} />
                </button>
                <button
                  onClick={() => setHistoryModal(null)}
                  style={{ background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer' }}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Search */}
            <div style={{ padding: '12px 20px', borderBottom: `1px solid ${colors.borderColor}` }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: colors.textTertiary }} />
                <input
                  value={historyPhoneFilter}
                  onChange={e => handleHistoryPhoneChange(e.target.value)}
                  placeholder="Buscar por telefone..."
                  style={{
                    ...styles.input,
                    paddingLeft: '32px', fontSize: '13px',
                    backgroundColor: colors.bgSecondary,
                  }}
                />
              </div>
            </div>

            {/* Logs list */}
            <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
              {historyLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Loader2 size={20} color={colors.accentPrimary} style={{ animation: 'spin 1s linear infinite' }} />
                </div>
              ) : historyLogs.length === 0 ? (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '40px', gap: '8px',
                }}>
                  <History size={32} color={colors.textTertiary} />
                  <p style={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}>
                    {historyPhoneFilter ? 'Nenhum envio encontrado para este número' : 'Nenhum envio registrado'}
                  </p>
                </div>
              ) : (
                <div>
                  {historyLogs.map((log, idx) => (
                    <div
                      key={log.id || idx}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 20px',
                        borderBottom: idx < historyLogs.length - 1 ? `1px solid ${colors.borderColor}` : 'none',
                        gap: '12px',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '13px', fontWeight: 500, color: colors.textPrimary,
                          fontFamily: 'monospace',
                        }}>
                          {log.recipient_phone}
                        </div>
                        <div style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '2px' }}>
                          {formatDate(log.sent_at)}
                        </div>
                        {log.status === 'failed' && log.error_message && (
                          <div style={{
                            fontSize: '11px', color: '#ef4444', marginTop: '4px',
                            lineHeight: '1.3',
                          }}>
                            {log.error_message}
                          </div>
                        )}
                      </div>
                      <LogStatusBadge status={log.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {historyTotal > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 20px', borderTop: `1px solid ${colors.borderColor}`,
                fontSize: '12px', color: colors.textTertiary,
              }}>
                <span>{historyTotal} envio{historyTotal !== 1 ? 's' : ''}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => handleHistoryPageChange(historyPage - 1)}
                    disabled={historyPage <= 1 || historyLoading}
                    style={{
                      ...styles.actionBtn, padding: '4px 8px',
                      opacity: historyPage <= 1 ? 0.4 : 1,
                    }}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span style={{ fontSize: '12px', color: colors.textSecondary }}>
                    {historyPage} / {totalPages || 1}
                  </span>
                  <button
                    onClick={() => handleHistoryPageChange(historyPage + 1)}
                    disabled={historyPage >= totalPages || historyLoading}
                    style={{
                      ...styles.actionBtn, padding: '4px 8px',
                      opacity: historyPage >= totalPages ? 0.4 : 1,
                    }}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default TemplatesPage;
